import { useEffect, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useTenant } from "@/contexts/TenantContext";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { 
  CreditCard, 
  QrCode, 
  CheckCircle2, 
  Calendar, 
  DollarSign,
  Loader2,
  AlertCircle,
  Package,
  MessageSquare,
  Sparkles
} from "lucide-react";
import { PaymentModal } from "@/components/subscription/PaymentModal";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { toast } from "sonner";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { ChangePlanModal } from "@/components/subscription/ChangePlanModal";

export default function SubscriptionManagement() {
  const { tenantId, subscriptionPlanId, subscriptionStatus, subscriptionType, subscriptionPaymentMethod, subscriptionNextBillingDate, refreshTenant, hasActiveSubscription } = useTenant();
  const [selectedPlan, setSelectedPlan] = useState<string | null>(null);
  const [showPlanSelection, setShowPlanSelection] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showChangePlanModal, setShowChangePlanModal] = useState(false);
  const queryClient = useQueryClient();

  // Buscar planos disponíveis do banco
  const { data: availablePlans, isLoading: plansLoading } = useQuery({
    queryKey: ["subscription-plans"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("subscription_plans")
        .select("*")
        .eq("status", "active")
        .order("price", { ascending: true });

      if (error) throw error;
      return data || [];
    },
  });

  // Buscar plano atual do tenant
  const { data: currentPlan } = useQuery({
    queryKey: ["current-plan", subscriptionPlanId],
    queryFn: async () => {
      if (!subscriptionPlanId) return null;
      const { data, error } = await supabase
        .from("subscription_plans")
        .select("*")
        .eq("id", subscriptionPlanId)
        .single();

      if (error) throw error;
      return data;
    },
    enabled: !!subscriptionPlanId,
  });

  // Buscar histórico de pagamentos
  const { data: payments, isLoading: paymentsLoading } = useQuery({
    queryKey: ["subscription-payments", tenantId],
    queryFn: async () => {
      if (!tenantId) return [];
      
      const { data, error } = await supabase
        .from("subscription_transactions")
        .select("*")
        .eq("tenant_id", tenantId)
        .order("created_at", { ascending: false });

      if (error) throw error;
      return data || [];
    },
    enabled: !!tenantId,
  });

  const planChanges = (payments || []).filter((tx: any) => tx.transaction_type === 'proration' || tx.transaction_type === 'plan_change_applied');

  // Buscar dados completos da assinatura
  const { data: tenantSubscription, isLoading: tenantLoading } = useQuery({
    queryKey: ["tenant-subscription", tenantId],
    queryFn: async () => {
      if (!tenantId) return null;
      
      const { data, error } = await supabase
        .from("tenants")
        .select("subscription_plan_id, subscription_status, subscription_type, subscription_payment_method, subscription_start_date, subscription_next_billing_date, subscription_last_payment_date, subscription_id, subscription_plan_pending_id, subscription_plan_pending_effective_date")
        .eq("id", tenantId)
        .single();

      if (error) throw error;
      return data;
    },
    enabled: !!tenantId,
  });

  const handlePlanSelect = (planId: string) => {
    if (subscriptionStatus === 'active' && subscriptionType !== 'regular') {
      toast.info("Entre em contato para alterar seu plano");
      return;
    }
    if (planId === subscriptionPlanId && subscriptionStatus === 'active') {
      toast.info("Você já está neste plano");
      return;
    }
    setSelectedPlan(planId);
    setShowPaymentModal(true);
  };

  const handlePaymentSuccess = async () => {
    setShowPaymentModal(false);
    setSelectedPlan(null);
    await refreshTenant?.();
    queryClient.invalidateQueries({ queryKey: ["subscription-payments"] });
    queryClient.invalidateQueries({ queryKey: ["tenant-subscription"] });
  };

  const formatCurrency = (value: number | null) => {
    if (!value) return "R$ 0,00";
    return new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: "BRL",
    }).format(value);
  };

  const formatDate = (date: string | null) => {
    if (!date) return "-";
    return format(new Date(date), "dd/MM/yyyy", { locale: ptBR });
  };

  const getStatusBadge = (status: string | null) => {
    switch (status) {
      case "approved":
        return <Badge className="bg-emerald-500">Aprovado</Badge>;
      case "pending":
        return <Badge className="bg-amber-500">Pendente</Badge>;
      case "rejected":
        return <Badge className="bg-red-500">Rejeitado</Badge>;
      default:
        return <Badge variant="outline">{status || "-"}</Badge>;
    }
  };

  const isActive = typeof hasActiveSubscription === 'function' ? !!hasActiveSubscription() : (subscriptionStatus === 'active' && !!subscriptionPlanId);
  const canManageSubscription = subscriptionType === 'regular';

  useEffect(() => {
    if (!isActive) setShowPlanSelection(true);
  }, [isActive]);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold">Gerenciamento de Assinatura</h1>
        <p className="text-muted-foreground mt-2">
          Gerencie seu plano, visualize pagamentos e altere sua assinatura
        </p>
      </div>

      {/* Assinatura Ativa */}
      {isActive && currentPlan && (
        <Card className={
          subscriptionType === 'cortesia' 
            ? "border-amber-500/30 bg-gradient-to-br from-amber-50 via-amber-50/80 to-yellow-50 dark:from-amber-950/30 dark:via-amber-900/20 dark:to-yellow-950/20 shadow-lg shadow-amber-500/10"
            : subscriptionType === 'testers'
            ? "border-blue-500/30 bg-gradient-to-br from-blue-50 via-cyan-50/80 to-sky-50 dark:from-blue-950/30 dark:via-cyan-900/20 dark:to-sky-950/20 shadow-lg shadow-blue-500/10"
            : "border-emerald-500/20 bg-emerald-50/50 dark:bg-emerald-950/20"
        }>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <CheckCircle2 className={
                    subscriptionType === 'cortesia' 
                      ? "h-5 w-5 text-amber-600 dark:text-amber-400"
                      : subscriptionType === 'testers'
                      ? "h-5 w-5 text-blue-600 dark:text-blue-400"
                      : "h-5 w-5 text-emerald-600"
                  } />
                  Assinatura Ativa - {currentPlan.name}
                </CardTitle>
                <CardDescription className="mt-2">
                  {currentPlan.name} - {currentPlan.description}
                </CardDescription>
              </div>
              <Badge className={
                subscriptionType === 'cortesia'
                  ? "bg-gradient-to-r from-amber-500 to-yellow-500 text-white shadow-md"
                  : subscriptionType === 'testers'
                  ? "bg-gradient-to-r from-blue-500 to-cyan-500 text-white shadow-md"
                  : "bg-emerald-500 text-white"
              }>
                {formatCurrency(currentPlan.price)}/mês
              </Badge>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <p className="text-sm text-muted-foreground">Tipo de Assinatura</p>
                <p className="font-semibold">
                  {subscriptionType === 'regular' && 'Premium'}
                  {subscriptionType === 'cortesia' && 'Cortesia'}
                  {subscriptionType === 'testers' && 'Beta Tester'}
                </p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Método de Pagamento</p>
                <p className="font-semibold">
                  {subscriptionType === 'cortesia' || subscriptionType === 'testers' 
                    ? 'Sem cobrança'
                    : subscriptionPaymentMethod === 'credit_card' 
                    ? 'Cartão de Crédito'
                    : subscriptionPaymentMethod === 'pix' 
                    ? 'PIX'
                    : subscriptionPaymentMethod === 'manual' 
                    ? 'Manual'
                    : '-'}
                </p>
              </div>
              {canManageSubscription && subscriptionNextBillingDate && (
                <div>
                  <p className="text-sm text-muted-foreground">Próxima Cobrança</p>
                  <p className="font-semibold">
                    {formatDate(subscriptionNextBillingDate)}
                  </p>
                </div>
              )}
            </div>
            
            {tenantSubscription?.subscription_start_date && (
              <div>
                <p className="text-sm text-muted-foreground">Data de Início</p>
                <p className="font-semibold">{formatDate(tenantSubscription.subscription_start_date)}</p>
              </div>
            )}

              <div className="pt-4 border-t grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h3 className="font-semibold mb-2">Recursos Incluídos:</h3>
                <ul className="space-y-1">
                  {(currentPlan.features as string[] || []).map((feature, index) => (
                    <li key={index} className="flex items-center gap-2 text-sm">
                      <CheckCircle2 className={
                        subscriptionType === 'cortesia'
                          ? "h-4 w-4 text-amber-600 dark:text-amber-400"
                          : subscriptionType === 'testers'
                          ? "h-4 w-4 text-blue-600 dark:text-blue-400"
                          : "h-4 w-4 text-emerald-600"
                      } />
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>
              
              {subscriptionType === 'regular' && (
                <div className="flex flex-col justify-center">
                  <div className="bg-gradient-to-br from-emerald-100 to-green-100 dark:from-emerald-900/40 dark:to-green-900/40 rounded-lg p-6 border border-emerald-300/50 dark:border-emerald-700/50">
                    <div className="flex items-start gap-3">
                      <div className="text-3xl">💚</div>
                      <div>
                        <h4 className="font-bold text-emerald-900 dark:text-emerald-100 mb-2 text-lg">
                          Plano Premium Ativo
                        </h4>
                        <p className="text-sm text-emerald-800 dark:text-emerald-200 leading-relaxed">
                          Parabéns! Você está aproveitando ao máximo o Caixa Fácil com acesso completo a todas as 
                          funcionalidades premium. Gerencie seu negócio com praticidade, tenha controle total do seu 
                          caixa e alcance resultados ainda melhores. 
                          <span className="font-semibold"> Seu sucesso é nossa prioridade! 💚</span>
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {subscriptionType === 'cortesia' && (
                <div className="flex flex-col justify-center">
                  <div className="bg-gradient-to-br from-amber-100 to-yellow-100 dark:from-amber-900/40 dark:to-yellow-900/40 rounded-lg p-6 border border-amber-300/50 dark:border-amber-700/50">
                    <div className="flex items-start gap-3">
                      <div className="text-3xl">✨</div>
                      <div>
                        <h4 className="font-bold text-amber-900 dark:text-amber-100 mb-2 text-lg">
                          Plano Especial de Cortesia
                        </h4>
                        <p className="text-sm text-amber-800 dark:text-amber-200 leading-relaxed">
                          Você é uma pessoa muito especial para nós! Este plano de cortesia é nosso presente para você, 
                          reconhecendo sua importância e valor. Aproveite todos os recursos premium sem custos, 
                          pois acreditamos no seu potencial e queremos fazer parte do seu sucesso. 
                          <span className="font-semibold"> Você merece o melhor! 💎</span>
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {subscriptionType === 'testers' && (
                <div className="flex flex-col justify-center">
                  <div className="bg-gradient-to-br from-blue-100 to-cyan-100 dark:from-blue-900/40 dark:to-cyan-900/40 rounded-lg p-6 border border-blue-300/50 dark:border-blue-700/50">
                    <div className="flex items-start gap-3">
                      <div className="text-3xl">🚀</div>
                      <div>
                        <h4 className="font-bold text-blue-900 dark:text-blue-100 mb-2 text-lg">
                          Plano Beta Tester - VIP
                        </h4>
                        <p className="text-sm text-blue-800 dark:text-blue-200 leading-relaxed">
                          Você foi selecionado(a) para ser um dos nossos testadores especiais! Sua opinião e feedback 
                          são extremamente valiosos para nós. Como beta tester, você tem acesso antecipado a todas as 
                          funcionalidades e faz parte do grupo que molda o futuro do sistema. 
                          <span className="font-semibold"> Obrigado por nos ajudar a crescer! 🌟</span>
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Ações de mudança de plano */}
              {canManageSubscription && (
                <div className="flex items-center gap-2">
                  <Button onClick={()=> setShowChangePlanModal(true)}>Mudar Plano</Button>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Mensagem para cortesia/testers */}
      {!canManageSubscription && isActive && (
        <Alert>
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            {subscriptionType === 'cortesia' 
              ? 'Você possui um plano de cortesia. Entre em contato para alterar seu plano.'
              : 'Você possui um Plano Beta Tester. Entre em contato para alterar seu plano.'}
          </AlertDescription>
        </Alert>
      )}

      {isActive && canManageSubscription && currentPlan && (
        <div className="flex justify-end">
          <Button variant="default" onClick={() => setShowPlanSelection((v) => !v)}>
            {showPlanSelection ? "Ocultar opções de plano" : "Alterar Plano"}
          </Button>
        </div>
      )}

      {/* Seleção de Planos */}
      {(!isActive || (isActive && showPlanSelection)) && (
        <Card>
          <CardHeader>
            <CardTitle>Escolha seu Plano</CardTitle>
            <CardDescription>
              Selecione o plano que melhor atende às suas necessidades
            </CardDescription>
          </CardHeader>
          <CardContent>
            {plansLoading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin" />
              </div>
            ) : availablePlans && availablePlans.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {availablePlans.map((plan: any) => {
                  const isCurrentPlan = subscriptionPlanId === plan.id && isActive;
                  const isRecommended = plan.id === 'plus_whats';

                  return (
                    <Card
                      key={plan.id}
                      className={`relative cursor-pointer transition-all hover:shadow-lg ${
                        isCurrentPlan
                          ? 'border-emerald-500 border-2'
                          : isRecommended
                          ? 'border-primary border-2'
                          : ''
                      }`}
                      onClick={() => !isCurrentPlan && handlePlanSelect(plan.id)}
                    >
                      {isRecommended && (
                        <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                          <Badge className="bg-primary text-primary-foreground">
                            Recomendado
                          </Badge>
                        </div>
                      )}
                      {isCurrentPlan && (
                        <div className="absolute -top-3 right-3">
                          <Badge className="bg-emerald-500 text-white">
                            Atual
                          </Badge>
                        </div>
                      )}
                      <CardHeader>
                        <div className="flex items-center gap-2 mb-2">
                          {plan.id === 'plus' && <Package className="h-5 w-5 text-primary" />}
                          {plan.id === 'plus_whats' && <MessageSquare className="h-5 w-5 text-primary" />}
                          {plan.id === 'plus_whats_ia' && <Sparkles className="h-5 w-5 text-primary" />}
                          <CardTitle>{plan.name}</CardTitle>
                        </div>
                        <div className="text-3xl font-bold text-primary">
                          {formatCurrency(plan.price)}
                          <span className="text-sm font-normal text-muted-foreground">/mês</span>
                        </div>
                        <CardDescription className="mt-2">{plan.description}</CardDescription>
                      </CardHeader>
                      <CardContent>
                        <ul className="space-y-2 mb-4">
                          {(plan.features as string[] || []).slice(0, 4).map((feature: string, index: number) => (
                            <li key={index} className="flex items-start gap-2 text-sm">
                              <CheckCircle2 className="h-4 w-4 text-emerald-600 mt-0.5 flex-shrink-0" />
                              <span>{feature}</span>
                            </li>
                          ))}
                          {(plan.features as string[] || []).length > 4 && (
                            <li className="text-sm text-muted-foreground">
                              +{(plan.features as string[] || []).length - 4} recursos adicionais
                            </li>
                          )}
                        </ul>
                        <Button
                          className="w-full"
                          variant={isCurrentPlan ? "outline" : "default"}
                          disabled={isCurrentPlan || !canManageSubscription}
                          onClick={(e) => {
                            e.stopPropagation();
                            if (!isCurrentPlan && canManageSubscription) handlePlanSelect(plan.id);
                          }}
                        >
                          {isCurrentPlan ? "Plano Atual" : "Selecionar Plano"}
                        </Button>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            ) : (
              <Alert>
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>
                  Nenhum plano disponível no momento
                </AlertDescription>
              </Alert>
            )}
          </CardContent>
        </Card>
      )}

      {/* Mudanças de Plano */}
      {planChanges.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Mudanças de Plano</CardTitle>
            <CardDescription>Detalhes de trocas, valores proporcionais e vigência</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {planChanges.map((tx: any) => {
                const m = tx.metadata || {};
                const meta = m.proration_meta || m;
                const fp = Number(meta.from_price || 0);
                const tp = Number(meta.to_price || 0);
                const delta = Number(meta.delta ?? (tp - fp));
                const cycleDays = Number(meta.cycle_days || 0);
                const daysLeft = Number(meta.days_left || 0);
                const effective = meta.effective_date ? new Date(meta.effective_date) : null;
                const charged = Number(tx.amount || 0);
                const isProration = tx.transaction_type === 'proration';
                const when = tx.payment_date || tx.created_at;
                return (
                  <div key={tx.id} className="p-3 border rounded">
                    <div className="flex items-center justify-between">
                      <div className="text-sm">
                        {isProration ? (
                          <>
                            <div>Upgrade imediato: cobrado proporcional de {new Intl.NumberFormat('pt-BR',{style:'currency',currency:'BRL'}).format(charged)}.</div>
                            <div>Fórmula: ({new Intl.NumberFormat('pt-BR',{style:'currency',currency:'BRL'}).format(tp)} - {new Intl.NumberFormat('pt-BR',{style:'currency',currency:'BRL'}).format(fp)}) × {daysLeft}/{cycleDays} = {new Intl.NumberFormat('pt-BR',{style:'currency',currency:'BRL'}).format(charged)}</div>
                            {effective && (<div>Próxima cobrança integral do novo plano em {effective.toLocaleDateString('pt-BR')}</div>)}
                          </>
                        ) : (
                          <>
                            <div>Troca de plano aplicada.</div>
                            {effective && (<div>Vigência iniciada em {effective.toLocaleDateString('pt-BR')}</div>)}
                          </>
                        )}
                      </div>
                      <div className="text-right text-sm">
                        <div>{new Intl.NumberFormat('pt-BR',{style:'currency',currency:'BRL'}).format(charged)}</div>
                        <div className="text-muted-foreground">{new Date(when).toLocaleString('pt-BR')}</div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Histórico de Pagamentos */}
      {!isActive && (
      <Card>
        <CardHeader>
          <CardTitle>Histórico de Pagamentos</CardTitle>
          <CardDescription>
            Visualize todos os pagamentos realizados
          </CardDescription>
        </CardHeader>
        <CardContent>
          {paymentsLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin" />
            </div>
          ) : payments && payments.length > 0 ? (
            <div className="space-y-4">
              {payments.map((payment: any) => {
                // Buscar dados do plano da transação
                const planId = payment.subscription_plan_id;
                return (
                  <div
                    key={payment.id}
                    className="flex items-center justify-between p-4 border rounded-lg"
                  >
                    <div className="flex items-center gap-4">
                      <div className="p-2 bg-primary/10 rounded-lg">
                        {payment.payment_method === 'credit_card' ? (
                          <CreditCard className="h-5 w-5 text-primary" />
                        ) : (
                          <QrCode className="h-5 w-5 text-primary" />
                        )}
                      </div>
                      <div>
                        <p className="font-semibold">
                          {formatCurrency(payment.amount)}
                        </p>
                        <p className="text-sm text-muted-foreground">
                          {formatDate(payment.payment_date || payment.created_at)}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {payment.payment_method === 'credit_card' ? 'Cartão de Crédito' : payment.payment_method === 'pix' ? 'PIX' : payment.payment_method || '-'}
                          {planId && ` • Plano: ${planId}`}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      {getStatusBadge(payment.status)}
                      {payment.mercadopago_payment_id && (
                        <p className="text-xs text-muted-foreground">
                          ID: {payment.mercadopago_payment_id.slice(0, 8)}...
                        </p>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <Alert>
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                Nenhum pagamento encontrado
              </AlertDescription>
            </Alert>
          )}
        </CardContent>
      </Card>
      )}

      {/* Modal de Pagamento */}
      {selectedPlan && (
        <PaymentModal
          open={showPaymentModal}
          onOpenChange={setShowPaymentModal}
          planId={selectedPlan}
          onSuccess={handlePaymentSuccess}
        />
      )}

      {/* Modal de Mudança de Plano */}
      {showChangePlanModal && (
        <ChangePlanModal
          open={showChangePlanModal}
          onOpenChange={setShowChangePlanModal}
          currentPlanId={currentPlan?.id || null}
        />
      )}
      {tenantSubscription?.subscription_plan_pending_id && (
        <div className="flex items-center justify-between p-4 border rounded-lg">
          <div className="text-sm">
            <div>
              Troca de plano agendada para {tenantSubscription.subscription_plan_pending_effective_date ? new Date(tenantSubscription.subscription_plan_pending_effective_date).toLocaleString('pt-BR') : '-'}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              onClick={async () => {
                await supabase.functions.invoke('subscription_changes', {
                  body: { action: 'cancel_scheduled', tenant_id: tenantId },
                });
                await refreshTenant?.();
                queryClient.invalidateQueries({ queryKey: ["tenant-subscription", tenantId] });
              }}
            >
              Cancelar agendamento
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
